/**
 * PricePositionArc — SVG arc gauge showing floor, asking price, and legacy range.
 *
 * Pure SVG. No canvas, no D3. All values in dollars.
 */

import { useMemo } from 'react';
import { T } from '@/styles/tokens';

// ─── Props ────────────────────────────────────────────────────

export interface PricePositionArcProps {
  floorPrice: number;         // breakeven floor in dollars
  askingPrice: number;        // user's asking price in dollars
  legacyLow: number;          // lowest legacy method result (dollars)
  legacyHigh: number;         // highest legacy method result (dollars)
  medium: string;             // e.g. "Oil"
  dimensions: string;         // e.g. '30" × 24"'
}

// ─── Constants ────────────────────────────────────────────────

const CX = 170;
const CY = 172;
const R_MAIN = 128;
const R_BAND = 152;
const STROKE_MAIN = 16;
const MONO = '"Manrope", "JetBrains Mono", "DM Sans", monospace';
const SANS = '"Manrope", "DM Sans", -apple-system, sans-serif';

const TEAL = '#2C9C8C';
const TEAL_DARK = '#1F7A6D';
const TEAL_LIGHT = '#3BB8A6';
const NAVY = '#243A4E';
const CORAL = '#E8735A';
const ORANGE = '#E07A3D';

// ─── Geometry helpers ─────────────────────────────────────────

function arcPoint(value: number, scaleMax: number, radius: number): { x: number; y: number } {
  const pct = Math.max(0, Math.min(1, value / scaleMax));
  const angle = Math.PI * (1 - pct);
  return { x: CX + radius * Math.cos(angle), y: CY - radius * Math.sin(angle) };
}

function floorRotation(value: number, scaleMax: number): number {
  const pct = Math.max(0, Math.min(1, value / scaleMax));
  const angleDeg = 180 * (1 - pct);
  return -(angleDeg - 90);
}

/**
 * Generate arc path as polyline segments instead of SVG `A` commands.
 * SVG arcs pick between two candidate circles, and the sweep flag
 * selects the wrong one when endpoints are far apart on the semicircle.
 * Polyline segments computed from our known center are always correct.
 */
function arcPath(from: number, to: number, scaleMax: number, radius: number): string {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const span = hi - lo;
  const steps = Math.max(24, Math.round(48 * span / scaleMax));
  const points: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const val = lo + (hi - lo) * (i / steps);
    const pt = arcPoint(val, scaleMax, radius);
    points.push(`${pt.x.toFixed(2)},${pt.y.toFixed(2)}`);
  }
  return `M ${points[0]} ` + points.slice(1).map(p => `L ${p}`).join(' ');
}

/** Radial offset: push a point outward (+) or inward (−) along arc angle */
function radialOffset(value: number, scaleMax: number, radius: number, dist: number): { x: number; y: number } {
  const pct = Math.max(0, Math.min(1, value / scaleMax));
  const angle = Math.PI * (1 - pct);
  return {
    x: CX + (radius + dist) * Math.cos(angle),
    y: CY - (radius + dist) * Math.sin(angle),
  };
}

/** Text anchor based on position on the arc: left side → end, right side → start, top → middle */
function autoAnchor(value: number, scaleMax: number): 'start' | 'middle' | 'end' {
  const pct = value / scaleMax;
  if (pct < 0.3) return 'end';
  if (pct > 0.7) return 'start';
  return 'middle';
}

function fmt(n: number): string {
  return '$' + n.toLocaleString('en-US');
}

/** Angular distance between two values on the arc (0–1 scale) */
function angularGap(a: number, b: number, scaleMax: number): number {
  return Math.abs(a / scaleMax - b / scaleMax);
}

// ─── Component ────────────────────────────────────────────────

export function PricePositionArc({
  floorPrice, askingPrice, legacyLow, legacyHigh, medium, dimensions,
}: PricePositionArcProps) {
  const scaleMax = useMemo(() => {
    const maxVal = Math.max(askingPrice, legacyHigh, floorPrice) * 1.4;
    return Math.max(Math.ceil(maxVal / 100) * 100, 200); // minimum $200 scale
  }, [askingPrice, legacyHigh, floorPrice]);

  // Key points
  const floorPt = arcPoint(floorPrice, scaleMax, R_MAIN);
  const pricePt = arcPoint(askingPrice, scaleMax, R_MAIN);
  const bandLoPt = arcPoint(legacyLow, scaleMax, R_BAND);
  const bandHiPt = arcPoint(legacyHigh, scaleMax, R_BAND);
  const bandLoMain = arcPoint(legacyLow, scaleMax, R_MAIN);
  const bandHiMain = arcPoint(legacyHigh, scaleMax, R_MAIN);
  const arcStart = arcPoint(0, scaleMax, R_MAIN);
  const arcEnd = arcPoint(scaleMax, scaleMax, R_MAIN);
  const floorRot = floorRotation(floorPrice, scaleMax);

  // Scale ticks (5 evenly spaced)
  const ticks = useMemo(() => {
    const count = 5;
    const step = scaleMax / (count + 1);
    return Array.from({ length: count }, (_, i) => {
      const val = step * (i + 1);
      const inner = arcPoint(val, scaleMax, R_MAIN - 10);
      const outer = arcPoint(val, scaleMax, R_MAIN + 10);
      return { inner, outer };
    });
  }, [scaleMax]);

  // ── Price label placement ─────────────────────────────────────
  // Rule: ALWAYS inside the arc. When too close to the FLOOR marker,
  // push the label further inward so it never overlaps.

  const priceFloorRatio = floorPrice > 0 ? askingPrice / floorPrice : 1;
  const isNearFloor = priceFloorRatio >= 0.95 && priceFloorRatio <= 1.05;

  // How close is the price dot to the floor marker on the arc? (0–1 scale)
  const gap = angularGap(askingPrice, floorPrice, scaleMax);
  // If gap < 0.12 (~22° on the semicircle), the default inward label
  // will collide with the FLOOR text. Push it deeper inside.
  const tooCloseToFloor = gap < 0.12 && !isNearFloor;
  const priceLabelInset = tooCloseToFloor ? -48 : -26;

  const showPriceLabel = !isNearFloor;
  const showFloorLabel = !isNearFloor;

  // Unique gradient IDs
  const uid = useMemo(() => Math.random().toString(36).slice(2, 6), []);

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox="0 0 340 210" style={{ display: 'block', width: '100%', overflow: 'visible' }}>
        <defs>
          {/* Band gradient */}
          <linearGradient id={`bandG-${uid}`} x1={bandLoPt.x} y1={bandLoPt.y} x2={bandHiPt.x} y2={bandHiPt.y} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={TEAL_LIGHT} />
            <stop offset="50%" stopColor={TEAL} />
            <stop offset="100%" stopColor={TEAL_DARK} />
          </linearGradient>

          {/* Background arc gradient */}
          <linearGradient id={`bgG-${uid}`} x1={arcStart.x} y1={arcStart.y} x2={arcEnd.x} y2={arcEnd.y} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ECEAE4" stopOpacity="1" />
            <stop offset="65%" stopColor="#ECEAE4" stopOpacity="1" />
            <stop offset="85%" stopColor="#ECEAE4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#ECEAE4" stopOpacity="0" />
          </linearGradient>

          {/* Coral gradient ($0 → floor) */}
          <linearGradient id={`coralG-${uid}`} x1={arcStart.x} y1={arcStart.y} x2={floorPt.x} y2={floorPt.y} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={CORAL} stopOpacity="0.55" />
            <stop offset="25%" stopColor={CORAL} stopOpacity="0.50" />
            <stop offset="100%" stopColor={CORAL} stopOpacity="0.85" />
          </linearGradient>

          {/* Teal gradient (floor → end) */}
          <linearGradient id={`tealG-${uid}`} x1={floorPt.x} y1={floorPt.y} x2={arcEnd.x} y2={arcEnd.y} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={TEAL} stopOpacity="0.92" />
            <stop offset="12%" stopColor={TEAL} stopOpacity="0.80" />
            <stop offset="35%" stopColor={TEAL} stopOpacity="0.55" />
            <stop offset="58%" stopColor={TEAL} stopOpacity="0.30" />
            <stop offset="78%" stopColor={TEAL} stopOpacity="0.12" />
            <stop offset="100%" stopColor={TEAL} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* 1. Legacy band (outer arc) */}
        <path d={arcPath(legacyLow, legacyHigh, scaleMax, R_BAND)} fill="none"
          stroke={`url(#bandG-${uid})`} strokeWidth="7" opacity="0.28" strokeLinecap="round" />
        <path d={arcPath(legacyLow, legacyHigh, scaleMax, R_BAND)} fill="none"
          stroke={`url(#bandG-${uid})`} strokeWidth="3" opacity="0.45" strokeLinecap="round" />

        {/* 2. Whisker lines */}
        <line x1={bandLoPt.x} y1={bandLoPt.y} x2={bandLoMain.x} y2={bandLoMain.y}
          stroke={TEAL} strokeWidth="0.8" opacity="0.2" />
        <line x1={bandHiPt.x} y1={bandHiPt.y} x2={bandHiMain.x} y2={bandHiMain.y}
          stroke={TEAL} strokeWidth="0.8" opacity="0.2" />

        {/* 3. Band endpoint labels — radially offset from band arc */}
        {(() => {
          const loLabel = radialOffset(legacyLow, scaleMax, R_BAND, 12);
          const hiLabel = radialOffset(legacyHigh, scaleMax, R_BAND, 12);
          return (
            <>
              <text x={loLabel.x} y={loLabel.y + 3}
                textAnchor={autoAnchor(legacyLow, scaleMax)}
                fontFamily={MONO} fontSize="8.5" fontWeight="700" fill={NAVY} opacity="0.72">
                {fmt(legacyLow)}
              </text>
              <text x={hiLabel.x} y={hiLabel.y + 3}
                textAnchor={autoAnchor(legacyHigh, scaleMax)}
                fontFamily={MONO} fontSize="8.5" fontWeight="700" fill={NAVY} opacity="0.72">
                {fmt(legacyHigh)}
              </text>
            </>
          );
        })()}

        {/* 4. Background arc (full semicircle) */}
        <path d={arcPath(0, scaleMax, scaleMax, R_MAIN)} fill="none"
          stroke={`url(#bgG-${uid})`} strokeWidth={STROKE_MAIN} strokeLinecap="butt" />

        {/* 5. Coral arc ($0 → floor) */}
        <path d={arcPath(0, floorPrice, scaleMax, R_MAIN)} fill="none"
          stroke={`url(#coralG-${uid})`} strokeWidth={STROKE_MAIN} strokeLinecap="round" />

        {/* 6. Teal arc (floor → end) */}
        <path d={arcPath(floorPrice, scaleMax, scaleMax, R_MAIN)} fill="none"
          stroke={`url(#tealG-${uid})`} strokeWidth={STROKE_MAIN} />

        {/* 7. Scale ticks */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.inner.x} y1={t.inner.y} x2={t.outer.x} y2={t.outer.y}
            stroke="#dcd8d4" strokeWidth="0.6" />
        ))}

        {/* 8. Floor marker (black pill) */}
        <g transform={`translate(${floorPt.x}, ${floorPt.y}) rotate(${floorRot})`}>
          <rect x="-3.5" y="-14" width="7" height="28" rx="3.5" fill="#0B0C13" opacity="0.80" />
        </g>

        {/* Floor label — radially inward from marker (suppressed when near floor) */}
        {showFloorLabel && (() => {
          const floorLabel = radialOffset(floorPrice, scaleMax, R_MAIN, -28);
          return (
            <text x={floorLabel.x} y={floorLabel.y + 3}
              textAnchor="middle"
              fontFamily="'Manrope', 'DM Sans', sans-serif" fontSize="7.5" fontWeight="800" fill="#777"
              letterSpacing="0.06em">
              FLOOR
            </text>
          );
        })()}

        {/* 9. Price dot — danger color when below floor */}
        {(() => {
          const belowFloor = askingPrice < floorPrice;
          const dotColor = belowFloor ? CORAL : TEAL;
          return (
            <>
              <circle cx={pricePt.x} cy={pricePt.y} r="12" fill="none" stroke={dotColor} strokeWidth="1" opacity="0.15" />
              <circle cx={pricePt.x} cy={pricePt.y} r="8" fill={dotColor} stroke="white" strokeWidth="3" />
              <circle cx={pricePt.x} cy={pricePt.y} r="2.5" fill="white" opacity="0.25" />
            </>
          );
        })()}

        {/* Price label — always inside the arc, pushed deeper when near FLOOR */}
        {showPriceLabel && (() => {
          const belowFloor = askingPrice < floorPrice;
          const labelColor = belowFloor ? CORAL : TEAL_DARK;
          const labelPt = radialOffset(askingPrice, scaleMax, R_MAIN, priceLabelInset);
          return (
            <text x={labelPt.x} y={labelPt.y + 4}
              textAnchor={autoAnchor(askingPrice, scaleMax)}
              fontFamily={MONO} fontSize="10" fontWeight="800" fill={labelColor}>
              {fmt(askingPrice)}
            </text>
          );
        })()}

        {/* 10. $0 label */}
        <text x={CX - R_MAIN - 8} y={CY + 20} textAnchor="start"
          style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 500, fill: '#c4c0ba' }}>
          {'$0'}
        </text>
      </svg>

      {/* Center overlay — floor price hero */}
      <div style={{
        position: 'absolute', bottom: 50, left: 0, right: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        pointerEvents: 'none',
      }}>
        <span style={{
          fontFamily: MONO, fontSize: 28, fontWeight: 800,
          color: NAVY, letterSpacing: '-0.02em',
        }}>
          {fmt(floorPrice)}
        </span>
        <span style={{
          fontFamily: SANS, fontSize: 8, fontWeight: 700,
          color: '#b4b0aa', letterSpacing: '0.12em',
          marginTop: 2,
        }}>
          BREAKEVEN FLOOR
        </span>
      </div>

      {/* Footer stats row */}
      <div style={{
        display: 'flex', borderTop: '1px solid #f0ebe6',
        marginTop: 8, paddingTop: 12, alignItems: 'baseline',
      }}>
        {/* Floor */}
        <div style={{ flex: '0 0 auto', textAlign: 'center', minWidth: 56 }}>
          <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' }}>
            {fmt(floorPrice)}
          </div>
          <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 600, color: '#b4b0aa', letterSpacing: '0.06em', marginTop: 2 }}>
            FLOOR
          </div>
        </div>

        {/* Common Methods — takes remaining space, single line */}
        <div style={{ flex: '1 1 auto', textAlign: 'center', minWidth: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            <span style={{ color: NAVY }}>{fmt(legacyLow)}</span>
            <span style={{ color: NAVY }}> – </span>
            <span style={{ color: NAVY }}>{fmt(legacyHigh)}</span>
          </div>
          <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 600, color: '#b4b0aa', letterSpacing: '0.06em', marginTop: 2 }}>
            COMMON METHODS
          </div>
        </div>

        {/* Your Price — danger color when below floor */}
        <div style={{ flex: '0 0 auto', textAlign: 'center', minWidth: 56 }}>
          <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: askingPrice < floorPrice ? CORAL : TEAL, whiteSpace: 'nowrap' }}>
            {fmt(askingPrice)}
          </div>
          <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 600, color: '#b4b0aa', letterSpacing: '0.06em', marginTop: 2 }}>
            YOUR PRICE
          </div>
        </div>
      </div>
    </div>
  );
}